import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControl,
  Box,
  Autocomplete,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import DeleteIcon from "@mui/icons-material/Delete";
import { GridColDef } from "@mui/x-data-grid";
import MUITable from "./MUITable";
import {
  getProjectName,
  processPc,
  deletePseudoCode,
  PseudoAsset,
  previewKnowledgeAsset,
  downloadAsset,
} from "../services/BackendServices";
import Chatbot from "../components/ChatBot/ChatBot";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { HTMLComponent } from "../components/HTMLComponent";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../redux/Hook";
import "./Styles.css";
import {
  ASSET_DOWNLOAD_FAILED,
  DELETE,
  DELETE_PSEUDO_DATA,
  ERROR_OCCURED,
  ERROR_PREVIEW_ASSET,
  ERROR_PROCESS_QA,
  FAILED_PSEUDO_CODE,
  INTERACT,
  PREVIEW,
  PROCESS_SUCCESS,
  PSEUDO_DELETE,
  PSEUDO_FAIL,
  QA_AUTOMATION_ERROR,
  UNEXPECTED_ERROR,
  UNEXPECTED_QA_ERROR,
  UNKNOWN_ERROR,
} from "../constants/constants";

const Pseudocode = () => {
  const [open, setOpen] = useState(false);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [projectOptions, setProjectOptions] = useState<string[]>([]);
  const [fileMap, setFileMap] = useState<{ qlik?: File }>({});
  const [openIframe, setOpenIframe] = useState(false);
  const [blobUrl, setBlobUrl] = useState("");
  const [assetName, setAssetName] = useState("");
  const [uploads, setUploads] = useState<
    Array<{
      id: number;
      asset_id: string;
      project_name: string;
      asset_name: string;
      creation_date: string;
      created_by: string;
    }>
  >([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [openChat, setOpenChat] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<{ asset_id: string }>({
    asset_id: "",
  });
  const [loading, setLoading] = useState(false);

  const notification = useAppSelector((state) => state.NotificationAlert);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchProjectNames();
    fetchPseudoAssetData();
  }, []);

  const fetchProjectNames = async () => {
    const response = await getProjectName();
    if (response?.success) {
      setProjectOptions(response?.data?.project_names);
    } else {
      setProjectOptions([]);
    }
  };

  const fetchPseudoAssetData = async () => {
    try {
      const response = await PseudoAsset();
      if (response?.success && response?.data?.mongo_bi_assets) {
        const mappedData = response?.data?.mongo_bi_assets?.map(
          (
            item: {
              asset_id: any;
              project_name: any;
              asset_name: any;
              creation_date: any;
              created_by: any;
            },
            index: number
          ) => ({
            id: index + 1,
            asset_id: item?.asset_id,
            project_name: item?.project_name,
            asset_name: item?.asset_name,
            creation_date: item?.creation_date,
            created_by: item?.created_by,
          })
        );
        setUploads(mappedData);
      } else {
        console.error(FAILED_PSEUDO_CODE, response?.message);
      }
    } catch (error) {
      console.error(ERROR_OCCURED, error);
    }
  };

  const handleDownloadAsset = async (assetId: string) => {
    setLoading(true);
    try {
      const response = await downloadAsset(assetId);
      if (response?.success && response?.data) {
        const blob = new Blob([response?.data], { type: "text/plain" });
        HTMLComponent(assetId, blob);
      } else {
        dispatch({
          type: "SEND_ALERT",
          data: {
            ...notification,
            enable: true,
            type: "error",
            message: response?.data?.message ?? response?.message,
            duration: 3000,
          },
        });
      }
    } catch (error) {
      dispatch({
        type: "SEND_ALERT",
        data: {
          ...notification,
          enable: true,
          type: "error",
          message: ASSET_DOWNLOAD_FAILED,
          duration: 3000,
        },
      });
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id: number) => {
    setLoading(true);
    const uploadToDelete = uploads?.find((upload) => upload?.id === id);
    if (!uploadToDelete) return;

    try {
      const response = await deletePseudoCode(uploadToDelete?.asset_id);
      if (response?.success) {
        setUploads(uploads?.filter((upload) => upload?.id !== id));
        dispatch({
          type: "SEND_ALERT",
          data: {
            ...notification,
            enable: true,
            type: "success",
            message: PSEUDO_DELETE,
            duration: 3000,
          },
        });
      } else {
        console.error(DELETE_PSEUDO_DATA, response?.error);
        dispatch({
          type: "SEND_ALERT",
          data: {
            ...notification,
            enable: true,
            type: "error",
            message: response?.error || PSEUDO_FAIL,
            duration: 3000,
          },
        });
      }
    } catch (error) {
      dispatch({
        type: "SEND_ALERT",
        data: {
          ...notification,
          enable: true,
          type: "error",
          message: UNEXPECTED_ERROR,
          duration: 3000,
        },
      });
    }
  };

  const handlePreview = async (id: number) => {
    const uploadToPreview = uploads?.find((upload) => upload?.id === id);
    if (!uploadToPreview) return;

    setLoading(true);
    try {
      const response: any = await previewKnowledgeAsset(
        uploadToPreview?.asset_id
      );
      HTMLComponent(uploadToPreview?.asset_id, response?.data);
    } catch (error) {
      console.error(ERROR_PREVIEW_ASSET, error);
    } finally {
      setLoading(false);
    }
  };

  const handleInteract = (id: number) => {
    const uploadToInteract = uploads?.find((upload) => upload?.id === id);
    if (!uploadToInteract) return;

    setSelectedAsset({ asset_id: uploadToInteract?.asset_id });
    setOpenChat(true);
  };

  const closeChatBot = () => {
    setSelectedAsset({ asset_id: "" });
    setOpenChat(false);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleMenuItemClick = (action: string) => {
    if (selectedId !== null) {
      if (action === DELETE) {
        handleDelete(selectedId);
      } else if (action === INTERACT) {
        handleInteract(selectedId);
      } else if (action === PREVIEW) {
        handlePreview(selectedId);
      }
    }
    handleMenuClose();
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    if (event?.target?.files && event?.target?.files?.length > 0) {
      const file = event?.target?.files[0];
      setFileMap((prev) => ({
        ...prev,
        [type]: file,
      }));
      if (type === "qlik" && !assetName) {
        setAssetName(file?.name);
      }
    }
  };

  const handleSubmit = async () => {
    if (!projectName || !fileMap?.qlik || !assetName) {
      return;
    }
  
    const formData = new FormData();
    formData?.append("project_name", projectName);
    formData?.append("file", fileMap?.qlik);
    formData?.append("asset_name", assetName);
    
    setLoading(true);
    try {
      const response = await processPc(formData);
      if (response?.success) {
        dispatch({
          type: "SEND_ALERT",
          data: {
            ...notification,
            enable: true,
            type: "success",
            message: PROCESS_SUCCESS,
            duration: 3000,
          },
        });
        fetchPseudoAssetData();
        setOpen(false);
        setAssetName("");
        setFileMap({});
        setProjectName(null);
      } else {
        dispatch({
          type: "SEND_ALERT",
          data: {
            ...notification,
            enable: true,
            type: "error",
            message: response?.message || UNKNOWN_ERROR,
            duration: 3000,
          },
        });
        console.error(ERROR_PROCESS_QA, response?.message || UNKNOWN_ERROR);
      }
    } catch (error) {
      dispatch({
        type: "SEND_ALERT",
        data: {
          ...notification,
          enable: true,
          type: "error",
          message: UNEXPECTED_ERROR,
          duration: 3000,
        },
      });
      console.error(UNEXPECTED_ERROR, error);
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "asset_id",
      headerName: "Pseudo Asset",
      flex: 2,
      renderCell: (params) => (
        <div
          className="asset-name-cursor-blue"
          onClick={() => handleDownloadAsset(params?.row?.asset_id)}
        >
          {params?.row?.asset_id}
        </div>
      ),
    },
    { field: "project_name", headerName: "Project", flex: 2 },
    { field: "creation_date", headerName: "Creation Date", flex: 1 },
    { field: "created_by", headerName: "Created By", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={(event) => handleMenuOpen(event, params?.row?.id)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Paper className="bi-automation-main">
      <Box className="bi-automation-add">
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          endIcon={<AddIcon />}
        >
          Add File
        </Button>
      </Box>

      <MUITable
        rows={uploads}
        columns={columns}
        tableHeight={450}
        loading={loading}
        defaultPageSize={10}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleMenuItemClick("preview")}>
          <ListItemIcon>
            <RemoveRedEyeIcon />
          </ListItemIcon>
          <ListItemText>Preview</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick("interact")}>
          <ListItemIcon>
            <SmartToyIcon />
          </ListItemIcon>
          <ListItemText>Interact</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick("delete")}>
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload Pseudocode File</DialogTitle>
        <DialogContent>
        {loading && <CircularProgress />}
          <FormControl fullWidth margin="normal">
            <Autocomplete
              options={projectOptions}
              getOptionLabel={(option) => option}
              value={projectName}
              onChange={(_, newValue) => setProjectName(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Project"
                  variant="outlined"
                />
              )}
            />
          </FormControl>

          <Box className="bi-automation-upload">
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              Upload Pseudocode
              <input
                type="file"
                hidden
                onChange={(e) => handleFileChange(e, "qlik")}
              />
            </Button>
            <Typography>{fileMap?.qlik?.name}</Typography>
          </Box>
          <FormControl fullWidth margin="normal">
            <TextField
              label="Asset Name"
              variant="outlined"
              value={assetName}
              onChange={(e) => setAssetName(e.target?.value)}
              fullWidth
            />
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!projectName || !fileMap?.qlik || !assetName}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <div className="qa-automation-chatbot-container">
        <Chatbot
          closeChatBot={closeChatBot}
          open={openChat}
          selectedAsset={selectedAsset?.asset_id}
        />
      </div>
      {openIframe ? (
        <iframe
          width="100%"
          height="100%"
          src={blobUrl}
          frameBorder="0"
        ></iframe>
      ) : null}
    </Paper>
  );
};

export default Pseudocode;
